import { useState, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { MapPin, Crosshair } from 'lucide-react';

const mapContainerStyle = {
    width: '100%',
    height: '300px'
};

const defaultCenter = {
    lat: import.meta.env.VITE_RESTAURANT_LAT,
    lng: import.meta.env.VITE_RESTAURANT_LNG
};

const libraries = ['places'];

export default function MapPicker({ onLocationSelect, selectedLocation, restaurantLocation }) {
    const [map, setMap] = useState(null);

    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
        libraries: libraries
    });

    const onMapClick = useCallback((e) => {
        const newPosition = {
            lat: e.latLng.lat(),
            lng: e.latLng.lng()
        };

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: newPosition }, (results, status) => {
            if (status === 'OK' && results[0]) {
                onLocationSelect({
                    lat: newPosition.lat,
                    lng: newPosition.lng,
                    address: results[0].formatted_address,
                    placeId: results[0].place_id
                });
            }
        });
    }, [onLocationSelect]);

    const onLoad = useCallback((map) => {
        setMap(map);
    }, []);

    const centerOnLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position) => {
                const pos = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                };
                map.panTo(pos);
            });
        }
    };

    if (loadError) {
        return (
            <div className="p-4 bg-red-50 text-red-700 rounded-lg">
                Failed to load Google Maps. Please check your API key.
            </div>
        );
    }

    if (!isLoaded) return <div className="h-[300px] flex items-center justify-center bg-gray-100">Loading map...</div>;

    const markerPosition = selectedLocation ?
        { lat: selectedLocation.lat, lng: selectedLocation.lng } :
        defaultCenter;

    return (
        <div className="space-y-2">
            <div className="relative rounded-lg overflow-hidden border">
                <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={markerPosition}
                    zoom={16}
                    onClick={onMapClick}
                    onLoad={onLoad}
                    options={{
                        streetViewControl: false,
                        mapTypeControl: false,
                        fullscreenControl: false
                    }}
                >
                    {restaurantLocation && (
                        <Marker
                            position={{
                                lat: restaurantLocation.latitude,
                                lng: restaurantLocation.longitude
                            }}
                            icon={{
                                url: 'http://maps.google.com/mapfiles/ms/icons/restaurant.png',
                                scaledSize: new window.google.maps.Size(40, 40)
                            }}
                            title="Restaurant"
                        />
                    )}

                    <Marker
                        position={markerPosition}
                        draggable={true}
                        onDragEnd={(e) => onMapClick(e)}
                        icon={{
                            url: 'http://maps.google.com/mapfiles/ms/icons/home.png',
                            scaledSize: new window.google.maps.Size(40, 40)
                        }}
                    />
                </GoogleMap>

                <button
                    type="button"
                    onClick={centerOnLocation}
                    className="absolute bottom-4 right-4 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100"
                    title="Center on my location"
                >
                    <Crosshair className="w-5 h-5 text-gray-700" />
                </button>
            </div>

            <p className="text-sm text-gray-600 flex items-center">
                <MapPin className="w-4 h-4 mr-1" />
                Click on map or drag marker to adjust location
            </p>
        </div>
    );
}